/**
 * Global Activity log screen — the tenant-wide, newest-first audit feed of
 * reservation and member-account actions, backed by `GET /v2/admin/audit`
 * (DNKADM-0151: server filters `?kind=`/`?contactId=`/`?fromDate=`/`?toDate=`, the
 * real logged action set; money events live in the Transaction ledger, not here).
 * Replaces the 0006 `activity` placeholder
 * at the same route. This screen is in the Operations group: available to any
 * authenticated operator (NOT RequireAdmin-gated) — the route placement is
 * unchanged.
 *
 * Layout (DNKADM-0035 U2): a compact, column-headed MUI `Table` — denser and more
 * scannable than the prior multi-line cards. Six columns, each degrading to an
 * em dash when its field is absent (the contract is additive and many fields are
 * nullable):
 *   - Time — the timestamp, compact ("Jun 18, 5:39 AM");
 *   - What — the HUMAN `detail` (`activityLabels.activityTitle` — NEVER the machine
 *     `action`) led by the kind glyph/tint affordance;
 *   - Member — `target.name ?? a short id`, linking to `/members/{contactId}` when
 *     a contact id resolves (the full raw contact id is never rendered as text);
 *   - Actor — `actorLineLabel` (name + the "(Front Desk)/(Open)" session-mode
 *     qualifier on a desk action, "(member app)" on a member/guest self-service one —
 *     "Member app"/"Guest" when the name did not resolve, NEVER a principal id
 *     (DNKADM-0394 U2); "System" for system rows), plus a `Platform support` chip on a row whose
 *     structured `actorPrincipalType` says so (DNKADM-0358 — the badge keys on the field,
 *     never on the label text);
 *   - Change — credit rows: signed `change` (green +/red −) · "balance N" · bucket ·
 *     location qualifier; purchase rows: money + plan/tier; otherwise an em dash;
 *   - Note — the human `note` pill ONLY when present AND it differs from the machine
 *     `reason` (so system import/refill/expiry rows show none).
 *
 * The Type filter's option set is FETCHED, not hand-maintained (DNKADM-0259): the
 * screen loads dink-api's published vocabulary (`GET /v2/admin/audit/kinds`) on mount
 * and groups it by the server's `category` under our headings, so the dropdown can
 * never drift from what the feed emits. Labels stay client-side (unlabelled values
 * humanize), and a failed fetch degrades to a static fallback list rather than an
 * empty or absent filter.
 *
 * Service-location names are loaded ONCE from the shipped `getServiceLocations()`
 * (DNKADM-0030 — reuse, no second fetch) into an id→name `Map` passed to
 * `locationLabel` and the search; a FAILED load fail-softs to `null` so the feed
 * still renders (the location label degrades to the raw id / "All locations"), the
 * feed being more important than the label.
 *
 * States: loading (skeleton table rows), empty (loaded, no entries), no-search-
 * match (a non-empty query over a non-empty page), and error (with a dedicated 403
 * "needs OWNER/ADMIN" notice) — the established `useResource` pattern, re-skinned to
 * the table (the empty/no-match cells span the full table width). Paging drives the
 * server `page` param (prev/next), the same convention as the Members list. The feed
 * re-sorts newest-first client-side defensively (the parser does this), so order is
 * robust to the wire order. Ledger change/balance are server-supplied, never
 * fabricated (`api/audit.ts` yields `null` when omitted).
 *
 * Toolbar and URL state (DNKADM-0430 P1-C): the shared `ListToolbar` carries the text
 * search, the Type and member filters and the date range, and each of them plus the page
 * lives in the URL (`activityListView.ts`), so a link or a reload reproduces the view.
 * The feed is server paged, so it has no sort control until server sort lands
 * (DNKAPI-0615). The text search still covers only the loaded page, and its placeholder
 * says so.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Link,
  ListSubheader,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { ListToolbar } from '@/components/list/ListToolbar';
import { parseDateParam } from '@/components/list/listDate';
import { parsePage, toPageParam, useListUrlState } from '@/components/list/useListUrlState';
import { Icon, MONO, inkAlpha, resolveTokens } from '@/theme';
import { buildActivityKindMaps } from '@/theme/maps/activity';
import { useResource } from '@/hooks/useResource';
import {
  listActivityAudit,
  listActivityKinds,
  type ActivityEntry,
  type ActivityActionFilter,
  type ActivityKindOption,
} from '@/api/audit';
import { getMember } from '@/api/members';
import { MemberNameLink } from '@/components/MemberNameLink';
import { MemberSearchDialog, type PickedContact } from '@/features/members/MemberSearchDialog';
import { getServiceLocations } from '@/api/operatingHours';
import { useServiceLocation } from '@/location/useServiceLocation';
import { formatCents } from '@/features/membership-credits/currency';
import { nonEmpty } from '@/utils/personLabel';
import { styleForKind } from './activityKind';
import {
  actorLineLabel,
  activityKindLabel,
  activityTitle,
  checkInPaymentChangeLabel,
  humanizeBucket,
  isRefusedAction,
  locationLabel,
  matchesActivityQuery,
  refusedActionMessage,
  type ServiceLocationNames,
} from './activityLabels';
import { roleLimitedMessage } from '@/components/accessCopy';
import {
  ACTIVITY_URL_DEFAULTS,
  ALL_KINDS,
  parseKindParam,
  parseMemberParam,
  toKindParam,
} from './activityListView';
import { ScreenError } from '@/components/ScreenError';
import { useCalendarZone } from '@/location/useCalendarZone';
import { DATE_TIME, formatInstant } from '@/utils/instantFormat';

const DEFAULT_PAGE_LIMIT = 20;
/** Skeleton rows shown on first load (matches the handoff feed rhythm). */
const SKELETON_ROWS = 6;
/** The six columns the table renders — the empty/no-match cells span them all. */
const COLUMN_COUNT = 6;
const PLACEHOLDER = '—';

/**
 * The Type filter's group headings — OUR copy for dink-api's `category` strings
 * (DNKADM-0259), in the fixed display order the dropdown renders. Domain, not actor:
 * the old "Member self" group encoded WHO acted, which no per-action category can ever
 * express (dink-api's census shows `reservation_cancel` written by both members and
 * admins). Actor stays per-row on `actorType`, rendered in the Actor column.
 */
const CATEGORY_HEADINGS: ReadonlyArray<{ category: string; heading: string }> = [
  { category: 'reservation', heading: 'Reservations' },
  { category: 'membership', heading: 'Memberships' },
  { category: 'credit', heading: 'Credits' },
  { category: 'order', heading: 'Orders & sales' },
  { category: 'pending_charge', heading: 'Pending charges' },
  { category: 'member', heading: 'Member record' },
  { category: 'staff', heading: 'Staff' },
];

/** Where an unrecognized (or absent) `category` buckets — never dropped, never a raw token. */
const OTHER_HEADING = 'Other';

/**
 * The Type filter's DEGRADED-PATH list, used only when `GET /v2/admin/audit/kinds`
 * fails or returns nothing usable — so the filter degrades rather than disappears.
 *
 * Seeded from the label-map keys onto dink-api's published categories, and sorted by
 * `value` so the degraded dropdown reads exactly like the live one (the route sorts the
 * same way). This list IS EXPECTED TO DRIFT from the route and that is acceptable: the
 * route is the source of truth and this is only what the screen shows when it cannot be
 * reached. Do not hand-extend it as new actions ship — fix the fetch instead.
 */
const FALLBACK_ACTIVITY_KINDS: readonly ActivityKindOption[] = [
  { value: 'coach_assign', category: 'member' },
  { value: 'coach_clear', category: 'member' },
  { value: 'credit_adjust', category: 'credit' },
  { value: 'email_change', category: 'member' },
  { value: 'lot_expiry_change', category: 'credit' },
  { value: 'lot_reduce', category: 'credit' },
  { value: 'member_added', category: 'member' },
  { value: 'member_guest_status_set', category: 'member' },
  { value: 'membership_set', category: 'membership' },
  { value: 'no_show_charge', category: 'order' },
  { value: 'package_granted', category: 'order' },
  { value: 'photo_delete', category: 'member' },
  { value: 'photo_set', category: 'member' },
  { value: 'profile_values_set', category: 'member' },
  { value: 'reservation_cancel', category: 'reservation' },
  { value: 'reservation_check_in', category: 'reservation' },
  { value: 'reservation_confirm', category: 'reservation' },
  { value: 'reservation_create', category: 'reservation' },
  { value: 'reservation_no_show', category: 'reservation' },
  { value: 'reservation_reschedule', category: 'reservation' },
];

interface KindGroup {
  /** The heading key — a `category` string, or {@link OTHER_HEADING} for the catch-all. */
  key: string;
  heading: string;
  options: ActivityKindOption[];
}

/**
 * Group the fetched vocabulary for the dropdown: the known categories in the fixed
 * display order above, then a single "Other" group last holding every unrecognized or
 * absent category. Server order is preserved within each group, and an empty group is
 * omitted (we never render a heading with nothing under it).
 */
function groupActivityKinds(options: readonly ActivityKindOption[]): KindGroup[] {
  const groups = new Map<string, KindGroup>();
  for (const { category, heading } of CATEGORY_HEADINGS) {
    groups.set(category, { key: category, heading, options: [] });
  }
  const other: KindGroup = { key: OTHER_HEADING, heading: OTHER_HEADING, options: [] };
  for (const option of options) {
    (groups.get(option.category) ?? other).options.push(option);
  }
  return [...groups.values(), other].filter((g) => g.options.length > 0);
}

/** Human label for the one-time / recurring purchase badge. */
const PURCHASE_TYPE_LABEL: Record<string, string> = {
  one_time: 'One-time',
  recurring: 'Recurring',
};

/**
 * Format an ISO/epoch timestamp for display (e.g. "Jun 10, 9:12 AM"). Falls back
 * to the raw string when it isn't parseable, and to a placeholder when absent —
 * never throws.
 */
function formatTime(ts: string | null, timeZone: string | null): string {
  // On the venue's clock (DNKADM-0476 F-05), not the browser's.
  return formatInstant(ts, timeZone, DATE_TIME, PLACEHOLDER);
}

/**
 * A short, display-safe target id — the last segment of a longer id (so a row
 * with no `target.name` can still show *something* without leaking the full raw
 * contact id). `null` for an absent/short-enough id we'd rather just drop.
 */
function shortTargetId(id: string | null): string | null {
  if (id === null) return null;
  // Only abbreviate clearly-opaque ids (long or delimited); leave short labels be.
  if (id.length <= 8 && !id.includes('-') && !id.includes(':')) return id;
  const tail = id.split(/[-:/]/).pop() ?? id;
  return tail.slice(-6);
}

/**
 * The human "why" pill text: the free-text `note`, shown ONLY when present AND it
 * differs from the machine `reason` (system rows like import/refill/expiry carry a
 * machine `reason` but no human note, so they get no pill). `null` → no pill.
 */
function notePill(entry: ActivityEntry): string | null {
  // A refused staff action carries the message the desk saw in `details` (DNKADM-0428).
  const note = entry.note ?? refusedActionMessage(entry);
  if (note === null) return null;
  return note === entry.reason ? null : note;
}

/**
 * The Type select's groups, plus the selected kind when the vocabulary does not list it
 * (a deep link to an action the live or fallback list lacks). It joins the "Other" group,
 * so the select shows a humanized label rather than an out-of-range blank.
 */
function withSelectedKind(groups: readonly KindGroup[], kind: string): KindGroup[] {
  if (kind === ALL_KINDS || groups.some((g) => g.options.some((o) => o.value === kind))) {
    return [...groups];
  }
  const selected: ActivityKindOption = { value: kind, category: OTHER_HEADING };
  const other = groups.find((g) => g.key === OTHER_HEADING);
  return other !== undefined
    ? groups.map((g) => (g === other ? { ...g, options: [...g.options, selected] } : g))
    : [...groups, { key: OTHER_HEADING, heading: OTHER_HEADING, options: [selected] }];
}

/** The member filter: the contact id from the URL, and its display name once known. */
interface MemberFilterValue {
  contactId: string;
  /** `null` until a deep-linked member's name resolves (the chip reads "Member"). */
  name: string | null;
}

export function ActivityLog() {
  // The view state lives in the URL (DNKADM-0430 P1-C): text search, Type, member, date
  // range and page, so a copied link or a reload reproduces the view. Writes replace the
  // history entry, so Back leaves the screen. Every raw param is validated here.
  const url = useListUrlState(ACTIVITY_URL_DEFAULTS);
  const setUrl = url.set;
  const page = parsePage(url.values.page);
  const kind = parseKindParam(url.values.kind);
  const contactId = parseMemberParam(url.values.member);
  const fromDate = parseDateParam(url.values.from);
  const toDate = parseDateParam(url.values.to);
  const search = url.values.q;
  const [pickerOpen, setPickerOpen] = useState(false);

  // The URL carries only the member's contact id. A pick in this session remembers the
  // name; a deep link resolves it below.
  const [memberName, setMemberName] = useState<MemberFilterValue | null>(null);
  const member: MemberFilterValue | null =
    contactId === null
      ? null
      : { contactId, name: memberName?.contactId === contactId ? memberName.name : null };
  const memberNameUnresolved = member !== null && member.name === null;

  // A deep-linked member's display name comes from the existing member-detail read
  // (`GET /v2/admin/members/{contactId}`), the same best-effort pattern as the
  // Transaction ledger's deep link (DNKADM-0379): a failure or an empty result leaves the
  // chip reading "Member", never clears the filter, and never blocks the feed (which keys
  // off the contact id alone).
  useEffect(() => {
    if (!memberNameUnresolved || contactId === null) return;
    let cancelled = false;
    void getMember(contactId)
      .then((detail) => {
        if (cancelled) return;
        const name = nonEmpty(detail.name) ?? nonEmpty(detail.email);
        if (name !== null) setMemberName({ contactId, name });
      })
      .catch(() => {
        /* silent by design, see above */
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, memberNameUnresolved]);

  // Location scope comes from the app-bar service-location chooser, not an in-screen
  // filter (DNKADM-0153, superseding the DNKADM-0152 Location select). Mirrors the
  // other Operations screens (Upcoming Charges / Transaction Ledger).
  const { selectedId } = useServiceLocation();

  // The server filters are `?kind=`, `?contactId=`, `?fromDate=`/`?toDate=`
  // (DNKADM-0151) and `?locationId=` (DNKAPI-0281 U1) — each sent only when set. The
  // app service location (`selectedId`) drives `?locationId=`; it is omitted while no
  // location is resolved (tenant-wide) and a location switch refetches (it is in the
  // deps). The client-side text search refines the loaded page.
  const activity = useResource(
    useCallback(
      () =>
        listActivityAudit({
          page,
          size: DEFAULT_PAGE_LIMIT,
          filters: {
            kind,
            ...(contactId !== null ? { contactId } : {}),
            ...(fromDate ? { fromDate } : {}),
            ...(toDate ? { toDate } : {}),
            ...(selectedId !== null ? { locationId: selectedId } : {}),
          },
        }),
      [page, kind, contactId, fromDate, toDate, selectedId],
    ),
    [page, kind, contactId, fromDate, toDate, selectedId],
  );

  // The Type filter's vocabulary (DNKADM-0259): `GET /v2/admin/audit/kinds`, loaded on
  // mount through the same `useResource` pattern. Deliberately NOT cached in module
  // scope or a context — dink-api serves it with `Cache-Control`/ETag and it is
  // tenant-invariant, changing only on deploy, so repeat cost belongs at the HTTP layer,
  // not in a hand-rolled cache that would need a test-reset seam. One small request per
  // visit to /activity is the right trade.
  const kinds = useResource(
    useCallback(() => listActivityKinds(), []),
    [],
  );
  // FAIL-SOFT: a failed/empty vocabulary falls back to the static list, so the Type
  // filter is never absent or empty and the feed is otherwise unaffected (no blocking
  // error). The fallback also stands in for the brief first-load window.
  const kindGroups = useMemo(() => {
    const fetched = kinds.status === 'ready' ? (kinds.data ?? []) : [];
    const groups = groupActivityKinds(fetched.length > 0 ? fetched : FALLBACK_ACTIVITY_KINDS);
    return withSelectedKind(groups, kind);
  }, [kinds.status, kinds.data, kind]);

  // The service-location id→name lookup (DNKADM-0035): loaded ONCE from the shipped
  // `getServiceLocations()` (reuse — no second fetch) and resolved to an id→name
  // `Map`. FAIL-SOFT: a failed/empty load yields `null`, which `locationLabel` and
  // the search degrade to the raw id / "All locations" — the feed must never break
  // over a missing location label.
  const locations = useResource(
    useCallback(() => getServiceLocations(), []),
    [],
  );
  const locationNames = useMemo<ServiceLocationNames | null>(() => {
    if (locations.status !== 'ready' || locations.data === null) return null;
    const map = new Map<string, string>();
    for (const loc of locations.data) {
      if (loc.name !== null) map.set(loc.id, loc.name);
    }
    return map;
  }, [locations.status, locations.data]);

  const entries = activity.data?.entries ?? [];
  const total = activity.data?.total ?? null;
  const pageLimit = activity.data?.pageLimit ?? DEFAULT_PAGE_LIMIT;

  // The text search is a CLIENT-SIDE filter over the currently-loaded page — it
  // composes with the server `?kind=` filter + paging, never re-fetches, and an
  // empty query shows all loaded rows (derived during render, no effect/state to
  // mirror). Tokenized AND-substring over the RENDERED fields (`activityLabels`),
  // including the location name so a search matches what the table shows.
  const trimmedSearch = search.trim();
  const visibleEntries =
    trimmedSearch.length === 0
      ? entries
      : entries.filter((e) => matchesActivityQuery(e, trimmedSearch, locationNames));
  // A no-match query over a non-empty loaded page is the empty-SEARCH state
  // (distinct from the loaded-but-no-entries empty state).
  const noSearchMatch =
    trimmedSearch.length > 0 && entries.length > 0 && visibleEntries.length === 0;

  // Any server-filter change restarts at page 1 (the page count differs per filter).
  // The text search only covers the loaded page, so it leaves the page alone.
  const changeSearch = (next: string) => setUrl({ q: next });
  const changeKind = (next: string) => setUrl({ kind: toKindParam(next), page: null });
  const changeFrom = (next: string) => setUrl({ from: next, page: null });
  const changeTo = (next: string) => setUrl({ to: next, page: null });
  const pickMember = (picked: PickedContact) => {
    setMemberName({ contactId: picked.contactId, name: picked.name });
    setUrl({ member: picked.contactId, page: null });
  };
  const clearMember = () => setUrl({ member: null, page: null });
  const clearAll = () =>
    setUrl({ q: null, kind: null, member: null, from: null, to: null, page: null });
  const setPage = (next: number) => setUrl({ page: toPageParam(next) });
  const canClear =
    search.length > 0 ||
    kind !== ALL_KINDS ||
    contactId !== null ||
    fromDate.length > 0 ||
    toDate.length > 0;

  return (
    <Box>
      <Typography variant="h1" component="h1">
        Activity log
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Every reservation and member-account action across this club, newest first. Audit reasons
        are captured at the time of the change. Payments and credit movements live in the
        Transaction ledger.
      </Typography>

      {/* The shared list toolbar (DNKADM-0430): search, Type and member filters, the date
          range and Clear. Server paged, so there is no sort control in Phase 1. The search
          box says it covers this page only, because it filters the loaded rows. */}
      <ListToolbar
        search={{
          value: search,
          onChange: changeSearch,
          placeholder: 'Search this page',
          label: 'Search activity on this page',
        }}
        filters={
          <>
            <TypeSelect value={kind} groups={kindGroups} onChange={changeKind} />
            <MemberFilter
              member={member}
              onOpen={() => setPickerOpen(true)}
              onClear={clearMember}
            />
          </>
        }
        dateRange={{
          from: fromDate,
          to: toDate,
          onFromChange: changeFrom,
          onToChange: changeTo,
        }}
        onClear={clearAll}
        canClear={canClear}
      />

      {pickerOpen && (
        <MemberSearchDialog onClose={() => setPickerOpen(false)} onPick={pickMember} />
      )}

      {activity.status === 'error' ? (
        <ScreenError
          variant="panel"
          error={activity.error}
          what="the activity log"
          onRetry={activity.refetch}
          forbiddenMessage={ACTIVITY_FORBIDDEN_MESSAGE}
          retryMessage="The activity log could not be loaded. Please try again."
          preferServerMessage
          sx={{ mt: 2 }}
        />
      ) : (
        <Paper elevation={1} sx={{ px: 2.5, py: 1, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" aria-label="Activity log">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>What</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activity.status === 'loading' &&
                  Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <SkeletonRow key={`sk-${i}`} />
                  ))}

                {activity.status === 'ready' && entries.length === 0 && (
                  <SpanRow>
                    <EmptyState page={page} />
                  </SpanRow>
                )}

                {activity.status === 'ready' && noSearchMatch && (
                  <SpanRow>
                    <NoSearchMatch query={trimmedSearch} onClear={() => changeSearch('')} />
                  </SpanRow>
                )}

                {activity.status === 'ready' &&
                  !noSearchMatch &&
                  visibleEntries.map((entry, i) => (
                    <ActivityRow
                      key={entry.id ?? `row-${i}`}
                      entry={entry}
                      locationNames={locationNames}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {activity.status === 'ready' && entries.length > 0 && (
            <Pagination
              page={page}
              total={total}
              pageLimit={pageLimit}
              onPrev={() => setPage(Math.max(1, page - 1))}
              onNext={() => setPage(page + 1)}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}

/**
 * The grouped Type `Select` — an "All activity" option (no `kind`) plus the fetched
 * vocabulary grouped by the server's `category` under our headings (DNKADM-0259). The
 * selected value drives the server `?kind=<action>` param (exact single match), which
 * dink-api guarantees accepts every value the vocabulary route returns; "All activity"
 * omits it. Option copy stays ours — {@link activityKindLabel} humanizes a value we
 * carry no label for, so a newly-shipped action is offered immediately.
 *
 * It sits in the shared toolbar's filter slot. It stays a local control rather than the
 * shared `ListSelectFilter` because that one has no group headings; the size, label and
 * surface match it.
 */
function TypeSelect({
  value,
  groups,
  onChange,
}: {
  value: ActivityActionFilter;
  groups: readonly KindGroup[];
  onChange: (next: ActivityActionFilter) => void;
}) {
  const t = resolveTokens(useTheme());
  return (
    <TextField
      select
      size="small"
      label="Type"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{ inputLabel: { shrink: true } }}
      sx={{ minWidth: 220, bgcolor: t.surface }}
    >
      <MenuItem value={ALL_KINDS}>All activity</MenuItem>
      {groups.flatMap((g) => [
        <ListSubheader key={`group-${g.key}`}>{g.heading}</ListSubheader>,
        ...g.options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {activityKindLabel(o.value)}
          </MenuItem>
        )),
      ])}
    </TextField>
  );
}

/**
 * The member filter: a removable chip when a member is picked, else a "Filter by member"
 * button. A deep-linked member reads "Member" until the name resolves; the raw contact id
 * is never shown as a person.
 */
function MemberFilter({
  member,
  onOpen,
  onClear,
}: {
  member: MemberFilterValue | null;
  onOpen: () => void;
  onClear: () => void;
}) {
  return member !== null ? (
    <Chip
      icon={<Icon name="person" sx={{ fontSize: 18 }} />}
      label={member.name !== null ? `Member: ${member.name}` : 'Member'}
      onDelete={onClear}
    />
  ) : (
    <Button
      size="small"
      variant="outlined"
      startIcon={<Icon name="person_search" sx={{ fontSize: 18 }} />}
      onClick={onOpen}
    >
      Filter by member
    </Button>
  );
}

/** A single full-width body row whose one cell spans every column (empty states). */
function SpanRow({ children }: { children: ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={COLUMN_COUNT} sx={{ borderBottom: 'none', p: 0 }}>
        {children}
      </TableCell>
    </TableRow>
  );
}

/**
 * Empty-SEARCH state (DNKADM-0029 U2): shown when a non-empty query matches no
 * loaded row. Distinct from the loaded-but-no-entries {@link EmptyState}; offers
 * a Clear affordance to restore all loaded rows.
 */
function NoSearchMatch({ query, onClear }: { query: string; onClear: () => void }) {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Icon name="search_off" sx={{ fontSize: 42, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 15, fontWeight: 500, mt: 1 }}>
        No activity matches “{query}”
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        The search filters the activity already loaded on this page.
      </Typography>
      <Button color="primary" startIcon={<Icon name="close" />} onClick={onClear} sx={{ mt: 1.5 }}>
        Clear search
      </Button>
    </Box>
  );
}

/**
 * One compact audit row. Each cell degrades to an em dash when its field is absent.
 * `locationNames` resolves the credit-row location qualifier (`null` = the location
 * list couldn't load → fail-soft to the raw id / "All locations").
 */
function ActivityRow({
  entry,
  locationNames,
}: {
  entry: ActivityEntry;
  locationNames: ServiceLocationNames | null;
}) {
  const theme = useTheme();
  const rowInk = inkAlpha(theme, 0.7);
  const calendarZone = useCalendarZone();
  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap', color: rowInk, verticalAlign: 'top' }}>
        {formatTime(entry.timestamp, calendarZone)}
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <WhatCell entry={entry} />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <MemberCell entry={entry} />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top', color: rowInk }}>
        <ActorCell entry={entry} />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <ChangeCell entry={entry} locationNames={locationNames} />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <NoteCell entry={entry} />
      </TableCell>
    </TableRow>
  );
}

/**
 * The "What" cell: kind glyph affordance + the human title (never the machine
 * action). On a reservation-related row carrying a `targetReservationUuid`
 * (DNKAPI-0281 U1, best-effort) the title becomes a `/reservations?reservation=<uuid>`
 * deep link (DNKADM-0147); absent the uuid it stays plain text — forward-compat
 * exactly like the Transaction ledger.
 */
function WhatCell({ entry }: { entry: ActivityEntry }) {
  const theme = useTheme();
  const tint = styleForKind(entry.kind, theme.palette.mode);
  const title = activityTitle(entry);
  const refused = isRefusedAction(entry);
  const t = resolveTokens(theme);
  const isReservationRow = entry.action !== null && entry.action.startsWith('reservation');
  const reservationUuid = isReservationRow ? entry.targetReservationUuid : null;
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box
        aria-hidden
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: refused ? t.warnSoft : tint.bg,
          color: refused ? t.warnSoftInk : tint.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={tint.icon} sx={{ fontSize: 14 }} />
      </Box>
      {reservationUuid !== null ? (
        <Link
          component={RouterLink}
          to={`/reservations?reservation=${encodeURIComponent(reservationUuid)}`}
          sx={{ fontSize: 13, fontWeight: 500 }}
        >
          {title}
        </Link>
      ) : (
        <Typography
          sx={{ fontSize: 13, fontWeight: 500, ...(refused ? { color: t.warnSoftInk } : {}) }}
        >
          {title}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * The "Member" cell: `target.name ?? a short id`, linking to `/members/{contactId}`
 * when a contact id resolves. The FULL raw contact id is never rendered as text —
 * the label is the name or a short tail; the id only rides in the link target.
 */
function MemberCell({ entry }: { entry: ActivityEntry }) {
  const contactId = entry.target.contactId;
  const label = entry.target.name ?? shortTargetId(contactId);
  if (label === null) return <Box component="span">{PLACEHOLDER}</Box>;
  return (
    <Box component="span" sx={{ fontSize: 13 }}>
      <MemberNameLink name={label} contactId={contactId} />
    </Box>
  );
}

/**
 * The "Actor" cell: `actorLineLabel` verbatim — including the ` (platform support)`
 * marker dink-api writes into the label, which is display text and is never parsed
 * (DNKADM-0357) — plus, on a support row, a small `Platform support` chip.
 *
 * The chip keys ONLY on the structured `actor.principalType` (DNKAPI-0513), never on the
 * label text: a row carrying the text marker with `actorPrincipalType: null` (or from a
 * server predating the field) renders exactly as it does today, with no chip. The text is
 * deliberately kept alongside it — the label is the accessible, copyable record; the chip
 * is the glance — so the chip repeats what the row already says and needs no `aria-label`.
 * Colors are the warn-soft token pair the shell's support-session chip already uses
 * (DNKADM-0356): both color modes, no new contrast pair. The row wraps rather than widening
 * the column, so a narrow (390px) viewport drops the chip under the label.
 *
 * Not gated on `entry.actor.type` either — the field alone decides. Under DNKAPI-0513 only an
 * `ADMIN` row can carry the marker, so an `actorType` gate would be unreachable; adding one
 * would just reintroduce a second way for the badge to disappear on a row that has the field.
 */
function ActorCell({ entry }: { entry: ActivityEntry }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  return (
    <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
      <Box component="span">{actorLineLabel(entry) ?? PLACEHOLDER}</Box>
      {entry.actor.principalType === 'PLATFORM_SUPPORT' && (
        <Chip
          size="small"
          label="Platform support"
          sx={{
            height: 20,
            fontSize: 11,
            bgcolor: t.warnSoft,
            color: t.warnSoftInk,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      )}
    </Stack>
  );
}

/**
 * The "Change" cell: credit rows show the signed change · "balance N" · humanized
 * bucket · location qualifier; purchase rows show the money + plan/tier; everything
 * else degrades to an em dash.
 */
function ChangeCell({
  entry,
  locationNames,
}: {
  entry: ActivityEntry;
  locationNames: ServiceLocationNames | null;
}) {
  const theme = useTheme();
  const hasLedger = entry.change !== null || entry.balance !== null;
  const hasPurchase =
    entry.amountCents !== null ||
    entry.plan !== null ||
    entry.tier !== null ||
    entry.term !== null ||
    entry.paymentRef !== null ||
    entry.purchaseType !== null;

  if (hasLedger) return <LedgerChange entry={entry} locationNames={locationNames} />;
  if (hasPurchase) return <PurchaseChange entry={entry} />;
  const checkInPayment = checkInPaymentChangeLabel(entry);
  if (checkInPayment !== null) {
    return <Box component="span" sx={{ fontSize: 12.5 }}>{checkInPayment}</Box>;
  }
  return (
    <Box
      component="span"
      sx={{ color: inkAlpha(theme, theme.palette.mode === 'dark' ? 0.51 : 0.4) }}
    >
      {PLACEHOLDER}
    </Box>
  );
}

/**
 * Credit-row change: signed `change` (green grant / red debit) · "balance N" ·
 * humanized bucket (`bay_reservation_credits` → "Bay reservation") · the location
 * qualifier ("All locations" / a resolved name / the fail-soft raw id). Each
 * segment drops when its field is absent.
 */
function LedgerChange({
  entry,
  locationNames,
}: {
  entry: ActivityEntry;
  locationNames: ServiceLocationNames | null;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const change = entry.change;
  const changeText = change !== null ? `${change > 0 ? '+' : ''}${change}` : null;
  // Positive = grant (green), negative = debit (red); 0/absent stays neutral.
  const changeColor =
    change !== null && change !== 0
      ? change > 0
        ? t.successSoftInk
        : t.error
      : inkAlpha(theme, 0.7);
  const bucket = humanizeBucket(entry.bucket);
  // The location qualifier is shown only for a scoped row OR the universal "All
  // locations" marker — i.e. always, since `locationLabel` maps `null` → "All
  // locations". Render it as a quiet trailing segment.
  const location = locationLabel(entry.serviceLocationId, locationNames);

  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, fontSize: 12.5, color: inkAlpha(theme, 0.7) }}
    >
      {changeText !== null && (
        <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: changeColor }}>
          {changeText}
        </Box>
      )}
      {entry.balance !== null && (
        <Box component="span" sx={{ color: t.textSubtle }}>
          {'· balance '}
          <Box component="span" sx={{ fontFamily: MONO }}>
            {entry.balance}
          </Box>
        </Box>
      )}
      {bucket !== null && (
        <Box component="span" sx={{ color: t.textSubtle }}>
          {'· '}
          {bucket}
        </Box>
      )}
      <Box component="span" sx={{ color: t.textMuted }}>
        {'· '}
        {location}
      </Box>
    </Stack>
  );
}

/**
 * Purchase-row change: amount (currency) · plan/tier/term · a one-time/recurring
 * badge · payment reference. Each segment is dropped when its field is absent
 * (never fabricated). Amount is rendered from integer cents via the shared
 * `currency.ts` `formatCents`.
 */
function PurchaseChange({ entry }: { entry: ActivityEntry }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const purchaseTint = buildActivityKindMaps(theme.palette.mode).purchase;
  const amount = entry.amountCents !== null ? formatCents(entry.amountCents) : null;
  // plan / tier / term form a single "· "-joined descriptor.
  const descriptor = [entry.plan, entry.tier, entry.term]
    .filter((s): s is string => typeof s === 'string' && s.length > 0)
    .join(' · ');
  const badge =
    entry.purchaseType !== null
      ? (PURCHASE_TYPE_LABEL[entry.purchaseType] ?? entry.purchaseType)
      : null;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        fontSize: 12.5,
        color: inkAlpha(theme, 0.75),
      }}
    >
      {amount !== null && (
        <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: purchaseTint.fg }}>
          {amount}
          {entry.currency !== null && entry.currency.toUpperCase() !== 'USD' && (
            <Box component="span" sx={{ ml: 0.5, fontWeight: 400, color: t.textSubtle }}>
              {entry.currency.toUpperCase()}
            </Box>
          )}
        </Box>
      )}
      {descriptor.length > 0 && (
        <Box component="span">
          {amount !== null && (
            <Box component="span" sx={{ color: inkAlpha(theme, 0.4), mr: 1 }}>
              ·
            </Box>
          )}
          {descriptor}
        </Box>
      )}
      {badge !== null && (
        <Chip
          size="small"
          label={badge}
          sx={{
            height: 20,
            fontSize: 11,
            bgcolor: purchaseTint.bg,
            color: purchaseTint.fg,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      )}
      {entry.paymentRef !== null && (
        <Box component="span" sx={{ fontFamily: MONO, fontSize: 11.5, color: inkAlpha(theme, 0.5) }}>
          {entry.paymentRef}
        </Box>
      )}
    </Stack>
  );
}

/**
 * The "Note" cell: the human "why" pill (the free-text `note`) — quoted, set off in
 * a soft block — ONLY when present and meaningful (differs from the machine
 * `reason`); otherwise the cell is empty.
 */
function NoteCell({ entry }: { entry: ActivityEntry }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const text = notePill(entry);
  if (text === null) return null;
  return (
    <Typography
      sx={{
        fontSize: 12,
        color: t.text2,
        px: 1.25,
        py: 0.5,
        bgcolor: t.raise,
        borderRadius: 1.5,
        borderLeft: `2px solid ${inkAlpha(theme, 0.15)}`,
        display: 'inline-block',
      }}
    >
      “{text}”
    </Typography>
  );
}

/** A shimmer table row matching the six columns (first load). */
function SkeletonRow() {
  return (
    <TableRow>
      <TableCell>
        <Skeleton width={90} height={12} />
      </TableCell>
      <TableCell>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton width={140} height={12} />
        </Stack>
      </TableCell>
      <TableCell>
        <Skeleton width={90} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={90} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={120} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={70} height={12} />
      </TableCell>
    </TableRow>
  );
}

function EmptyState({ page }: { page: number }) {
  const theme = useTheme();
  return (
    <Box sx={{ py: 7, textAlign: 'center' }}>
      <Icon name="history" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
        No recorded activity{page > 1 ? ' on this page.' : ' yet.'}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 360, mx: 'auto' }}>
        Reservation and member-account changes across the club will appear here, newest first.
      </Typography>
    </Box>
  );
}

function Pagination({
  page,
  total,
  pageLimit,
  onPrev,
  onNext,
}: {
  page: number;
  total: number | null;
  pageLimit: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  // `total` passes through only when present; without it we cannot know the last
  // page, so "Next" is offered until the end is known (same as the Members list).
  const t = resolveTokens(useTheme());
  const lastPage = total !== null ? Math.max(1, Math.ceil(total / pageLimit)) : null;
  const atLastPage = lastPage !== null && page >= lastPage;

  return (
    <Stack
      component="nav"
      aria-label="Pagination"
      direction="row"
      sx={{
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        px: 0.5,
        py: 1.5,
        borderTop: `1px solid ${t.lineFaint}`
      }}>
      <Typography variant="body2" aria-current="page">
        Page {page}
      </Typography>
      <IconAction label="Previous page" size="small" onClick={onPrev} disabled={page <= 1}>
        <Icon name="chevron_left" />
      </IconAction>
      <IconAction label="Next page" size="small" onClick={onNext} disabled={atLastPage}>
        <Icon name="chevron_right" />
      </IconAction>
    </Stack>
  );
}

/** The activity log's own "why and who to ask" for a `403` (DNKADM-0440). */
const ACTIVITY_FORBIDDEN_MESSAGE = roleLimitedMessage('The activity log', 'OWNER or ADMIN');
